import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { UserPlus, Check, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Reusable mutual-connection button. Renders the correct state
// (Connect / Requested / Accept+Decline / Connected) for the viewing creator.
export function ConnectButton({ userId, creatorName = "this creator", initial, onChange, className = "" }) {
  const { user } = useAuth();
  const [state, setState] = useState(initial || null);
  const [busy, setBusy] = useState(false);

  const isCreator = user?.role === "creator";
  const isSelf = user?.id === userId;

  useEffect(() => {
    if (!isCreator || isSelf || initial) return;
    let active = true;
    api.get(`/connections/status/${userId}`).then((r) => { if (active) setState(r.data); }).catch(() => {});
    return () => { active = false; };
  }, [userId, isCreator, isSelf, initial]);

  if (!isCreator || isSelf) return null;
  const status = state?.status || "none";
  const set = (data) => { setState(data); onChange?.(data); };
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); }
    catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };

  const connect = () => act(async () => { const r = await api.post(`/connections/request/${userId}`); set(r.data); toast.success("Connection request sent"); });
  const cancel = () => act(async () => { const r = await api.delete(`/connections/${userId}`); set(r.data); });
  const accept = () => act(async () => { const r = await api.post(`/connections/${state.connection_id}/accept`); set(r.data); toast.success(`You're connected with ${creatorName}`); });
  const decline = () => act(async () => { const r = await api.post(`/connections/${state.connection_id}/decline`); set(r.data); });
  const remove = () => { if (!window.confirm(`Remove your connection with ${creatorName}?`)) return; act(async () => { const r = await api.delete(`/connections/${userId}`); set(r.data); toast.success("Connection removed"); }); };

  const spin = busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null;

  if (status === "incoming") {
    return (
      <div className={`flex gap-2 ${className}`}>
        <Button data-testid={`connect-accept-${userId}`} disabled={busy} onClick={accept} className="h-9 rounded-full bg-emerald-600 hover:bg-emerald-700">
          {spin || <Check className="mr-1.5 h-4 w-4" />} Accept
        </Button>
        <Button data-testid={`connect-decline-${userId}`} disabled={busy} onClick={decline} variant="outline" className="h-9 rounded-full">
          Decline
        </Button>
      </div>
    );
  }
  if (status === "requested") {
    return (
      <Button data-testid={`connect-requested-${userId}`} disabled={busy} onClick={cancel} variant="outline" className={`h-9 rounded-full ${className}`}>
        {spin || <Clock className="mr-1.5 h-4 w-4" />} Requested
      </Button>
    );
  }
  if (status === "connected") {
    return (
      <Button data-testid={`connect-connected-${userId}`} disabled={busy} onClick={remove} variant="outline"
        className={`group h-9 rounded-full border-emerald-200 text-emerald-700 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 ${className}`}>
        {spin || <Check className="mr-1.5 h-4 w-4" />}
        <span className="group-hover:hidden">Connected</span>
        <span className="hidden group-hover:inline">Remove</span>
      </Button>
    );
  }
  return (
    <Button data-testid={`connect-btn-${userId}`} disabled={busy} onClick={connect} className={`h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 ${className}`}>
      {spin || <UserPlus className="mr-1.5 h-4 w-4" />} Connect
    </Button>
  );
}
